import React, { forwardRef } from 'react';
import type { ListItemTextProps as MuiListItemTextProps } from '@mui/material';
import { ListItemText as MuiListItemText } from '@mui/material';
import { isEmpty } from 'lodash';

import { reportErrorInProductionOrThrow } from '~/Utils';

/**
 This Component is a wrapper on MUI component.
 This component should be imported and not MUI directly!
**/

/**
 * Explicit ListItemText props based on actual usage in the codebase.
 */
export interface ListItemTextProps {
  /** The main content element */
  primary?: React.ReactNode;
  /** The secondary content element */
  secondary?: React.ReactNode;
  /** Props forwarded to the primary Typography element */
  primaryTypographyProps?: MuiListItemTextProps['primaryTypographyProps'];
  /** Props forwarded to the secondary Typography element */
  secondaryTypographyProps?: MuiListItemTextProps['secondaryTypographyProps'];
  /** Override or extend the styles applied to the component */
  classes?: MuiListItemTextProps['classes'];
  /** Callback fired when the element is clicked */
  onClick?: React.MouseEventHandler<HTMLElement>;
}

const ListItemText = forwardRef<HTMLDivElement, ListItemTextProps>(
  ({ primary, secondary, primaryTypographyProps, secondaryTypographyProps, classes, onClick, ...restProps }, ref) => {
    if (!isEmpty(restProps)) {
      reportErrorInProductionOrThrow(
        `Unexpected props passed to ListItemText: ${Object.keys(restProps).join(', ')}.
Examine if these need to be removed, or added explicitly to the ListItemTextProps interface and to the component's signature.`,
        true
      );
    }

    return (
      <MuiListItemText
        primary={primary}
        secondary={secondary}
        primaryTypographyProps={primaryTypographyProps}
        secondaryTypographyProps={secondaryTypographyProps}
        classes={classes}
        onClick={onClick}
        ref={ref}
        {...restProps}
      />
    );
  }
);

ListItemText.displayName = 'ListItemText';

export default ListItemText;
